import React, {useRef, useState} from 'react';
import style from './style.module.scss';
import ReactDOM from 'react-dom';

interface DynamicTooltipProps {
  children: React.ReactNode;
  tooltipText: React.ReactNode;
  tooltipStyle?: React.CSSProperties;
  onVisibleChange?: (visible: boolean) => void;
}

export const DynamicTooltip: React.FC<DynamicTooltipProps> = ({children, tooltipText, tooltipStyle, onVisibleChange}) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'top-left' | 'top-right' | 'bottom' | 'bottom-left' | 'bottom-right'>('top');
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{top: number; left: number} | null>(null);
  const showTooltip = () => {
    setVisible(true);
    if (onVisibleChange) onVisibleChange(true);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX,
      });
    }
  };
  const hideTooltip = () => {
    setVisible(false);
    if (onVisibleChange) onVisibleChange(false);
  };
  const updateTooltipPosition = React.useCallback(() => {
    if (containerRef.current && tooltipRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const tooltipWidth = tooltipRef.current.offsetWidth;
      const tooltipHeight = tooltipRef.current.offsetHeight;
      const margin = 8;
      const centerX = rect.left + rect.width / 2 + window.scrollX;
      const spaceAbove = rect.top;
      const leftEdge = centerX - tooltipWidth / 2;
      const rightEdge = centerX + tooltipWidth / 2;
      let newPosition: typeof position = 'top';
      let top = 0;
      let left = centerX;

      // Default: try top
      if (spaceAbove > tooltipHeight + margin) {
        top = rect.top + window.scrollY - tooltipHeight - margin;
        // Check for left/right overflow
        if (leftEdge < 0) {
          newPosition = 'top-right';
          left = rect.left + window.scrollX + 8; // 8px padding from left
        } else if (rightEdge > window.innerWidth) {
          newPosition = 'top-left';
          left = rect.right + window.scrollX - 8 - tooltipWidth; // 8px padding from right
        } else {
          newPosition = 'top';
        }
      } else {
        // Use bottom
        top = rect.bottom + window.scrollY + margin;
        if (leftEdge < 0) {
          newPosition = 'bottom-right';
          left = rect.left + window.scrollX + 8;
        } else if (rightEdge > window.innerWidth) {
          newPosition = 'bottom-left';
          left = rect.right + window.scrollX - 8 - tooltipWidth;
        } else {
          newPosition = 'bottom';
        }
      }

      setPosition(newPosition);
      setCoords({top, left});
    }
  }, []);

  React.useEffect(() => {
    if (visible) {
      updateTooltipPosition();
      window.addEventListener('scroll', updateTooltipPosition, true);
      window.addEventListener('resize', updateTooltipPosition);
      return () => {
        window.removeEventListener('scroll', updateTooltipPosition, true);
        window.removeEventListener('resize', updateTooltipPosition);
      };
    }
  }, [visible, updateTooltipPosition]);

  return (
    <div
      className={style.tooltipContainer}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      ref={containerRef}
    >
      {children}
      {visible && coords && tooltipText &&
        ReactDOM.createPortal(
          <div
            className={[
              style.tooltipBox,
              position === 'top' && style.tooltipBoxTop,
              position === 'top-left' && style.tooltipBoxTopLeft,
              position === 'top-right' && style.tooltipBoxTopRight,
              position === 'bottom' && style.tooltipBoxBottom,
              position === 'bottom-left' && style.tooltipBoxBottomLeft,
              position === 'bottom-right' && style.tooltipBoxBottomRight,
            ].filter(Boolean).join(' ')}
            ref={tooltipRef}
            style={{
              position: 'absolute',
              top: coords.top,
              left: coords.left,
              transform:
                position === 'top' || position === 'bottom' ?
                  'translateX(-50%)' :
                  position === 'top-left' || position === 'bottom-left' ?
                    'translateX(0)' :
                    position === 'top-right' || position === 'bottom-right' ?
                      'translateX(-100%)' :
                      undefined,
              maxWidth: '250px',
              fontSize: '12px',
              zIndex: 9999,
              height: 'fit-content',
              ...tooltipStyle,
            }}
          >
            {tooltipText}
          </div>,
          document.body,
        )
      }
    </div>
  );
};
